import { ROUTE_PERMS, hasPerm } from '@/admin/auth/permissions';
import { usePermStore } from '@/admin/auth/usePermStore';

/** Terminal target when the user can open none of the candidate pages. */
const NO_ACCESS = '/admin/no-access';

/**
 * Ordered list of routes to try when redirecting after login.
 * First match (user has the required permission) wins.
 *
 * Canonical (non-param) entry points only — every key here MUST exist in
 * `ROUTE_PERMS`, because unmapped entries are now skipped (fail-closed).
 */
const REDIRECT_CANDIDATES = [
  '/admin/dashboard',
  '/admin/orders',
  '/admin/live-orders',
  '/admin/stores',
  '/admin/items',
  '/admin/users',
  '/admin/eagle-view',
  '/admin/drivers',
  '/admin/categories',
  '/admin/collections-log',
  '/admin/store-payouts',
  '/admin/store-balance',
  '/admin/wallet-transactions',
  '/admin/transactions',
  '/admin/ratings',
  '/admin/mobile',
  '/admin/app-categories',
  '/admin/featured-products',
  '/admin/featured-vendors',
  '/admin/best-products',
  '/admin/other-stores',
  '/admin/operational-areas',
  '/admin/promo-codes',
  '/admin/delivery-fee-promotions',
  '/admin/push-notifications',
  '/admin/cod-bans',
  '/admin/staff',
  '/admin/roles',
  '/admin/audit-logs',
  '/admin/settings',
  '/admin/reports',
];

/**
 * Returns the first route the current user has permission to access, or
 * `/admin/no-access` when nothing matches.
 *
 * Fail-closed: an unmapped candidate is SKIPPED, never treated as public —
 * otherwise a missing `ROUTE_PERMS` key silently sends the user to a page they
 * cannot open (and the guard then bounces them to no-access anyway).
 *
 * NOTE: only returns a page the user can actually open, so it is safe as the
 * `PermissionGuard` redirect target / loop-breaker.
 */
export function getFirstAccessibleRoute(): string {
  const perms = usePermStore.getState().permissions;

  // AdminAuthGuard hydrates perms before rendering, so an empty list means a
  // genuinely role-less account (backend fail-closed) — do not guess a page.
  if (!perms || perms.length === 0) return NO_ACCESS;

  // Wildcard users (super_admin) get the dashboard directly.
  if (perms.includes('*')) return '/admin/dashboard';

  for (const route of REDIRECT_CANDIDATES) {
    const required = ROUTE_PERMS[route];
    if (!required || required.length === 0) {
      // Unmapped (or the deliberate public escape hatch): skip, never assume
      // access.
      continue;
    }
    // anyOf / AND-group aware (see RequiredPerms)
    if (hasPerm(perms, required)) return route;
  }

  return NO_ACCESS;
}
